import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Dialog, DialogPanel, DialogBackdrop } from '@headlessui/react'
import Close from '../../assets/close.svg?react'
import { BlackActionButton } from '../buttons/BlackActionButton'
import { createPendingFromApproved, getPendingByApprovedRef } from '../../pocketbase/PocketbasePendingSubmissionsService'

interface ConfirmEditSubmissionDialogProps {
    isOpen: boolean
    onClose: () => void
    submissionId: string | null
}

export const ConfirmEditSubmissionDialog = ({
    isOpen,
    onClose,
    submissionId,
}: ConfirmEditSubmissionDialogProps) => {
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleConfirm = async () => {
        if (!submissionId) {
            return
        }

        try {
            setIsLoading(true)
            setError(null)

            // First check if there is already a pending submission for this approved submission
            const existingPendingId = await getPendingByApprovedRef(submissionId)

            if (existingPendingId) {
                // If it exists, show an error message
                setError('This submission already has an editing version pending approval.')
                setIsLoading(false)
                return
            }

            // Create a pending submission from the approved one
            const pendingSubmission = await createPendingFromApproved(submissionId)

            // Navigate to the submit page with the new pending submission ID
            navigate(`/submit?id=${pendingSubmission.id}`)

            // Close the dialog
            onClose()
        } catch (error) {
            console.error('Error creating edit submission:', error)
            setError('Failed to create edit submission. Please try again.')
            setIsLoading(false)
        }
    }

    const handleClose = () => {
        if (!isLoading) {
            setError(null)
            onClose()
        }
    }

    return (
        <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
            <DialogBackdrop className="fixed inset-0 bg-black/30" />

            <div className="fixed inset-0 flex items-center justify-center p-4">
                <DialogPanel className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative">
                    {/* Close button */}
                    {!isLoading && (
                        <button
                            onClick={handleClose}
                            className="absolute cursor-pointer top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <Close width={16} height={16} />
                        </button>
                    )}

                    {/* Title */}
                    <h2 className="text-xl font-semibold mb-2 mt-6">Edit Approved Submission</h2>

                    {/* Description */}
                    <p className="text-gray-500 text-sm mb-6">
                        Your edits will need to be approved again before the changes are reflected on the live site. Do you want to continue?
                    </p>

                    {/* Error Message */}
                    {error && (
                        <p className="text-sm text-red-600 mb-4">{error}</p>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={handleClose}
                            disabled={isLoading}
                            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <BlackActionButton
                            label={isLoading ? 'Loading...' : 'Continue to Edit'}
                            onClick={handleConfirm}
                        />
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    )
}
